// src/components/FormBuilder/FormBuilder.js
import React from "react";
import "./FormBuilder.css";

// Default shape for new forms
export const emptyCustomForm = {
  enabled: false,
  title: "",
  description: "",
  confirmationEmailSubject: "",
  confirmationEmailBody: "",
  questions: [],
};

export default function FormBuilder({ value, onChange }) {
  const formConfig = value || emptyCustomForm;

  const update = (patch) => {
    const next = { ...formConfig, ...patch };
    onChange(next);
  };

  const handleMetaChange = (e) => {
    const { name, value } = e.target;
    update({ [name]: value });
  };

  const handleQuestionChange = (index, patch) => {
    const questions = [...(formConfig.questions || [])];
    const existing = questions[index] || {};
    questions[index] = { ...existing, ...patch };
    update({ questions });
  };

  const toggleRequired = (index) => {
    const questions = [...(formConfig.questions || [])];
    const q = questions[index];
    if (!q) return;
    questions[index] = { ...q, required: !q.required };
    update({ questions });
  };

  // --- New option helpers ---

  const addOption = (qIndex) => {
    const questions = [...(formConfig.questions || [])];
    const q = questions[qIndex] || {};
    const options = Array.isArray(q.options) ? [...q.options] : [];
    options.push("");
    questions[qIndex] = { ...q, options };
    update({ questions });
  };

  const changeOption = (qIndex, optIndex, value) => {
    const questions = [...(formConfig.questions || [])];
    const q = questions[qIndex];
    if (!q) return;
    const options = Array.isArray(q.options) ? [...q.options] : [];
    options[optIndex] = value;
    questions[qIndex] = { ...q, options };
    update({ questions });
  };

  const removeOption = (qIndex, optIndex) => {
    const questions = [...(formConfig.questions || [])];
    const q = questions[qIndex];
    if (!q) return;
    const options = Array.isArray(q.options) ? [...q.options] : [];
    options.splice(optIndex, 1);
    questions[qIndex] = { ...q, options };
    update({ questions });
  };

  const addQuestion = () => {
    const questions = [...(formConfig.questions || [])];
    const newQuestion = {
      id: Date.now().toString() + "-" + Math.random().toString(16).slice(2),
      label: "",
      type: "short-text",
      required: false,
      options: [],
    };
    questions.push(newQuestion);
    update({ questions });
  };

  const removeQuestion = (index) => {
    const questions = [...(formConfig.questions || [])];
    questions.splice(index, 1);
    update({ questions });
  };

  return (
    <div className="fb-root">
      {/* Meta */}
      <div className="fb-field">
        <label>Form Title (optional)</label>
        <input
          name="title"
          type="text"
          value={formConfig.title || ""}
          onChange={handleMetaChange}
          placeholder="e.g. Living Room Inquiry Intake"
        />
      </div>

      <div className="fb-field">
        <label>Form Description (optional)</label>
        <input
          name="description"
          type="text"
          value={formConfig.description || ""}
          onChange={handleMetaChange}
          placeholder="Short instructions for registrants..."
        />
      </div>

      <div className="fb-field">
        <label>Confirmation Email Subject (optional)</label>
        <input
          name="confirmationEmailSubject"
          type="text"
          value={formConfig.confirmationEmailSubject || ""}
          onChange={handleMetaChange}
          placeholder="e.g. You are registered for {eventName}"
        />
      </div>

      <div className="fb-field">
        <label>Confirmation Email Message (optional)</label>
        <textarea
          name="confirmationEmailBody"
          value={formConfig.confirmationEmailBody || ""}
          onChange={handleMetaChange}
          placeholder={
            "Hi {name}, thanks for registering for {eventName}.\nYou are all set."
          }
        />
        <p className="fb-hint">
          Tokens: {"{name}"}, {"{eventName}"}, {"{eventDate}"}, {"{eventLink}"}.
        </p>
      </div>

      {/* Header */}
      <div className="fb-header">
        <h3>Questions</h3>
        <button type="button" className="fb-button" onClick={addQuestion}>
          + Add Question
        </button>
      </div>

      {/* Empty */}
      {(!formConfig.questions || formConfig.questions.length === 0) && (
        <p className="fb-hint">No questions yet.</p>
      )}

      {/* Questions */}
      {formConfig.questions?.map((q, qIndex) => (
        <div key={q.id || qIndex} className="fb-question-card">
          <div className="fb-field">
            <label>Question Text</label>
            <input
              type="text"
              value={q.label || ""}
              onChange={(e) =>
                handleQuestionChange(qIndex, { label: e.target.value })
              }
              placeholder="e.g. What brings you to this session?"
            />
          </div>

          <div className="fb-field">
            <label>Answer Type</label>
            <select
              value={q.type || "short-text"}
              onChange={(e) =>
                handleQuestionChange(qIndex, {
                  type: e.target.value,
                  // If switching away from multi-options, clear options
                  ...(e.target.value === "multiple-choice" ||
                  e.target.value === "checkboxes"
                    ? {}
                    : { options: [] }),
                })
              }
            >
              <option value="short-text">Short text</option>
              <option value="long-text">Long text</option>
              <option value="multiple-choice">Multiple choice</option>
              <option value="checkboxes">Checkboxes</option>
            </select>
          </div>

          {/* Options as individual lines */}
          {(q.type === "multiple-choice" || q.type === "checkboxes") && (
            <div className="fb-field">
              <label>
                {q.type === "multiple-choice"
                  ? "Choices"
                  : "Checkbox options"}
              </label>

              {(q.options && q.options.length > 0) && (
                <div className="fb-options-list">
                  {q.options.map((opt, optIndex) => (
                    <div
                      key={optIndex}
                      className="fb-option-row"
                    >
                      <input
                        type="text"
                        value={opt}
                        onChange={(e) =>
                          changeOption(qIndex, optIndex, e.target.value)
                        }
                        placeholder={
                          q.type === "multiple-choice"
                            ? `Choice ${optIndex + 1}`
                            : `Option ${optIndex + 1}`
                        }
                      />
                      <button
                        type="button"
                        className="fb-button-danger"
                        onClick={() => removeOption(qIndex, optIndex)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <button
                type="button"
                className="fb-button"
                onClick={() => addOption(qIndex)}
              >
                {q.type === "multiple-choice"
                  ? "+ Add Choice"
                  : "+ Add Checkbox"}
              </button>

              <p className="fb-hint">
                These will appear as selectable options for attendees.
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="fb-q-footer">
            <label className="fb-check">
              <input
                type="checkbox"
                checked={!!q.required}
                onChange={() => toggleRequired(qIndex)}
              />
              Required
            </label>

            <button
              type="button"
              className="fb-button-danger"
              onClick={() => removeQuestion(qIndex)}
            >
              Delete question
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
